import React, { useState, useMemo } from 'react';
import { objections } from '../../data/scripts';
import { useWorkspaceMerge } from '../../contexts/WorkspaceMergeContext';
import parseScriptTokens from './parseScriptTokens';
import { FiSearch, FiChevronDown, FiChevronRight } from 'react-icons/fi';

export default function ObjectionPanel() {
  const { fields, setMergeField, language } = useWorkspaceMerge();
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('');
  const isEs = language === 'es';

  const filtered = useMemo(() => {
    if (!filter.trim()) return objections;
    const term = filter.toLowerCase();
    return objections.filter((o) => {
      const label = isEs ? (o.label_es || o.label) : o.label;
      const content = isEs ? (o.content_es || o.content) : o.content;
      return label.toLowerCase().includes(term) || content.toLowerCase().includes(term);
    });
  }, [filter, isEs]);

  const toggle = (id) => setExpandedId((prev) => (prev === id ? null : id));

  return (
    <div className="flex flex-col h-full">
      {/* ── Header / Search ────────────────────────────────────── */}
      <div className="px-4 pt-4 pb-3 border-b border-gray-200 shrink-0">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">
          {isEs ? 'Manejador de Objeciones' : 'Objection Handler'}
        </h3>
        <div className="relative">
          <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={isEs ? 'Filtrar objeciones…' : 'Filter objections…'}
            className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-md text-xs focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
          />
        </div>
      </div>

      {/* ── Accordion list ─────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-6">
            {isEs ? 'Ninguna objeción coincide con tu filtro.' : 'No objections match your filter.'}
          </p>
        )}

        {filtered.map((obj) => {
          const isOpen = expandedId === obj.id;
          const displayLabel = isEs ? (obj.label_es || obj.label) : obj.label;
          const displayContent = isEs ? (obj.content_es || obj.content) : obj.content;
          return (
            <div
              key={obj.id}
              className="border-b border-gray-100 last:border-b-0"
            >
              <button
                type="button"
                onClick={() => toggle(obj.id)}
                className="w-full flex items-start gap-2 px-4 py-3 text-left hover:bg-gray-50 transition-colors group"
              >
                {isOpen ? (
                  <FiChevronDown className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                ) : (
                  <FiChevronRight className="h-4 w-4 text-gray-400 mt-0.5 shrink-0 group-hover:text-gray-600" />
                )}
                <span
                  className={`text-xs font-semibold leading-snug ${
                    isOpen ? 'text-primary' : 'text-gray-700'
                  }`}
                >
                  {displayLabel}
                </span>
              </button>

              {isOpen && (
                <div className="px-4 pb-4 pl-10">
                  <div className="text-[13px] leading-[1.7] text-gray-700 bg-gray-50 rounded-lg p-4">
                    {parseScriptTokens(displayContent, fields, setMergeField)}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
